import type { TieredPricingConfig, CustomerEquipmentTypeResponse } from '@/types';

/**
 * Tier definitions for display and calculation
 */
export const PRICING_TIERS = [
  { name: '1 day', minDays: 1, maxDays: 1, field: 'price1Day' as const, newItemField: 'newItemPrice1Day' as const },
  { name: '2-3 days', minDays: 2, maxDays: 3, field: 'price2To3Days' as const, newItemField: 'newItemPrice2To3Days' as const },
  { name: '4-7 days', minDays: 4, maxDays: 7, field: 'price4To7Days' as const, newItemField: 'newItemPrice4To7Days' as const },
  { name: '8-14 days', minDays: 8, maxDays: 14, field: 'price8To14Days' as const, newItemField: 'newItemPrice8To14Days' as const },
  { name: '15-21 days', minDays: 15, maxDays: 21, field: 'price15To21Days' as const, newItemField: 'newItemPrice15To21Days' as const },
  { name: '22-31 days', minDays: 22, maxDays: 31, field: 'price22To31Days' as const, newItemField: 'newItemPrice22To31Days' as const },
] as const;

export type TierField = typeof PRICING_TIERS[number]['field'];

/**
 * Get the lowest available tier price for "From €X" display
 */
export function getLowestTierPrice(pricing: TieredPricingConfig): number | null {
  const prices = [
    pricing.price1Day,
    pricing.price2To3Days,
    pricing.price4To7Days,
    pricing.price8To14Days,
    pricing.price15To21Days,
    pricing.price22To31Days,
  ].filter((p): p is number => p != null && p > 0);

  return prices.length > 0 ? Math.min(...prices) : null;
}

/**
 * Get the best tier for a given number of days
 */
export function getTierForDays(
  days: number,
  pricing: TieredPricingConfig
): { tierName: string; price: number } | null {
  // Find the tier that fits this duration
  for (const tier of [...PRICING_TIERS].reverse()) {
    if (days >= tier.minDays && days <= tier.maxDays) {
      const price = pricing[tier.field];
      if (price != null && price > 0) {
        return { tierName: tier.name, price };
      }
    }
  }

  // If exact match not found, find smallest tier that covers the days
  for (const tier of PRICING_TIERS) {
    if (days <= tier.maxDays) {
      const price = pricing[tier.field];
      if (price != null && price > 0) {
        return { tierName: tier.name, price };
      }
    }
  }

  return null;
}

/**
 * Calculate tiered price with tier stacking for >40 days
 * Uses greedy algorithm: largest tier first
 */
export function calculateTieredPrice(
  days: number,
  pricing: TieredPricingConfig
): { tiers: Array<{ tierName: string; count: number; tierPrice: number }>; totalPrice: number } | null {
  if (days <= 0) return null;

  const tiers: Array<{ tierName: string; count: number; tierPrice: number }> = [];
  let remainingDays = days;
  let totalPrice = 0;

  // Reverse to start with largest tier
  const sortedTiers = [...PRICING_TIERS].reverse();

  while (remainingDays > 0) {
    let tierApplied = false;

    for (const tier of sortedTiers) {
      const price = pricing[tier.field];
      if (price == null || price <= 0) continue;

      // For tiers other than 1-day, only apply if we have enough days
      if (remainingDays >= tier.minDays) {
        const daysConsumed = tier.maxDays;

        // Find if this tier already exists in our applied tiers
        const existingTier = tiers.find(t => t.tierName === tier.name);
        if (existingTier) {
          existingTier.count++;
        } else {
          tiers.push({ tierName: tier.name, count: 1, tierPrice: price });
        }

        totalPrice += price;
        remainingDays -= Math.min(daysConsumed, remainingDays);
        tierApplied = true;
        break;
      }
    }

    // If no tier was applied, use 1-day tier or fallback
    if (!tierApplied) {
      const oneDayPrice = pricing.price1Day;
      if (oneDayPrice != null && oneDayPrice > 0) {
        const existingTier = tiers.find(t => t.tierName === '1 day');
        if (existingTier) {
          existingTier.count += remainingDays;
        } else {
          tiers.push({ tierName: '1 day', count: remainingDays, tierPrice: oneDayPrice });
        }
        totalPrice += oneDayPrice * remainingDays;
        remainingDays = 0;
      } else {
        // No pricing available
        return null;
      }
    }
  }

  return { tiers, totalPrice };
}

/**
 * Get all available tiers with prices for display
 */
export function getAvailableTiers(
  pricing: TieredPricingConfig,
  product?: CustomerEquipmentTypeResponse,
  isNewItem?: boolean
): Array<{ tierName: string; minDays: number; maxDays: number; price: number }> {
  return PRICING_TIERS
    .map(tier => {
      // If new item pricing, try new item field first, fall back to standard
      let price: number | undefined;
      if (isNewItem && product?.allowNewItemRental) {
        price = product[tier.newItemField] ?? pricing[tier.field];
      } else {
        price = pricing[tier.field];
      }

      if (price != null && price > 0) {
        return {
          tierName: tier.name,
          minDays: tier.minDays,
          maxDays: tier.maxDays,
          price,
        };
      }
      return null;
    })
    .filter((t): t is NonNullable<typeof t> => t !== null);
}

/**
 * Check if product has tiered pricing configured
 */
export function hasTieredPricing(pricing: TieredPricingConfig): boolean {
  return getAvailableTiers(pricing).length > 0;
}

// =====================================================================
// ACCESSORY PRICING HELPERS
// Accessories use the same tiered pricing structure as equipment types
// =====================================================================

/**
 * Check if accessory has tiered pricing configured
 */
export function hasAccessoryTieredPricing(accessory: TieredPricingConfig): boolean {
  return hasTieredPricing(accessory);
}

/**
 * Get the lowest tier price for an accessory (for "From €X" display)
 */
export function getLowestAccessoryTierPrice(accessory: TieredPricingConfig): number | null {
  return getLowestTierPrice(accessory);
}

/**
 * Get the best tier for a given number of days for an accessory
 */
export function getAccessoryTierForDays(
  days: number,
  accessory: TieredPricingConfig
): { tierName: string; price: number } | null {
  return getTierForDays(days, accessory);
}

/**
 * Get all available tiers for an accessory
 */
export function getAccessoryAvailableTiers(
  accessory: TieredPricingConfig
): Array<{ tierName: string; minDays: number; maxDays: number; price: number }> {
  return getAvailableTiers(accessory);
}
